import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useIsAuthenticated } from '@azure/msal-react';
import axios from 'axios';
import { getConfiguration } from '../shared/utils/configuration';
import { getRequestHeaders } from './utils';
import { UserDetailsDTO } from '@kotipizzagroup/kotipizza-products-api-client';
import { getUserDetails } from './products-api';
import { UserRolesResponse } from '@kotipizzagroup/kotipizza-orders-api-client';
import { getOrdersRoles } from './orders-api';
import { getUserPermissions, getUserRoles } from './users-api';
import { GetUserPermissionsResponse, UserRolesDto } from '@kotipizzagroup/kotipizza-users-api-client';
import { UserRolesDto as ShopsUserRolesDto } from '@kotipizzagroup/kotipizza-shops-api-admin-client';
import { getUserShopsApiRoles } from './shops-api';

const twentyFourHoursInMs = 1000 * 60 * 60 * 24;

const fetchUserPermissionsCampaignsApi = async (): Promise<UserRolesResponse> => {
  const apiRoot = getConfiguration<string>('campaignsService.root', '');
  const { data } = await axios.get(`${apiRoot}/user/roles`, { headers: await getRequestHeaders() });

  return data;
};

const fetchUserPermissionsLoyaltyApi = async (): Promise<UserRolesResponse> => {
  const apiRoot = getConfiguration<string>('loyaltyService.root', '');
  const { data } = await axios.get(`${apiRoot}/admin/user/scopes`, { headers: await getRequestHeaders() });
  return data;
};

const fetchUserPermissionsFromShopsApi = async (): Promise<ShopsUserRolesDto> => {
  return await getUserShopsApiRoles();
};

const fetchUserPermissionsFromOrdersApi = async (): Promise<UserRolesResponse> => {
  return await getOrdersRoles();
};

const fetchUserRolesFromUsersApi = async (): Promise<UserRolesDto> => {
  return await getUserRoles();
};

const fetchUserPermissions = async (): Promise<GetUserPermissionsResponse> => {
  return await getUserPermissions();
};

const GetPermissionsQueryOptions = (isAuthenticated: boolean) => {
  return {
    enabled: isAuthenticated,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    staleTime: twentyFourHoursInMs,
    retryOnMount: false,
    retry: false,
  };
};

export type UserPermissionsHookResult = {
  roles: string[];
  shops: string[];
  isErrorCampaigns: boolean;
  isLoadingCampaigns: boolean;
  isErrorLoyalty: boolean;
  isLoadingLoyalty: boolean;
  isLoadingProducts: boolean;
  isErrorProducts: boolean;
  isLoadingShops: boolean;
  isErrorShops: boolean;
  isLoadingOrders: boolean;
  isErrorOrders: boolean;
  isErrorUsers: boolean;
  isLoading: boolean;
};

export const useUserPermissions = (): UserPermissionsHookResult => {
  const isAuthenticated = useIsAuthenticated();

  const queryCampaignsPermissions = useQuery<UserRolesResponse, Error>(
    ['campaigns.permissions'],
    fetchUserPermissionsCampaignsApi,
    GetPermissionsQueryOptions(isAuthenticated)
  );

  const queryLoyaltyPermissions = useQuery<UserRolesResponse, Error>(
    ['loyalty.permissions'],
    fetchUserPermissionsLoyaltyApi,
    GetPermissionsQueryOptions(isAuthenticated)
  );

  const queryProductsPermissions = useQuery<UserDetailsDTO, Error>(
    ['products.permissions'],
    getUserDetails,
    GetPermissionsQueryOptions(isAuthenticated)
  );

  const queryShopsPermissions = useQuery<ShopsUserRolesDto, Error>(
    ['shops.permissions'],
    fetchUserPermissionsFromShopsApi,
    GetPermissionsQueryOptions(isAuthenticated)
  );

  const queryOrdersPermissions = useQuery<UserRolesResponse, Error>(
    ['orders.permissions'],
    fetchUserPermissionsFromOrdersApi,
    GetPermissionsQueryOptions(isAuthenticated)
  );

  const queryUserPermissions = useQuery<GetUserPermissionsResponse, Error>(
    ['user.permissions'],
    fetchUserPermissions,
    GetPermissionsQueryOptions(isAuthenticated)
  );

  const queryUserRoles = useQuery<UserRolesDto, Error>(
    ['user.roles'],
    fetchUserRolesFromUsersApi,
    GetPermissionsQueryOptions(isAuthenticated)
  );

  const roles = useMemo(
    () => [
      ...(queryCampaignsPermissions.data?.roles || []),
      ...(queryLoyaltyPermissions.data?.roles || []),
      ...(queryProductsPermissions.data?.roles || []),
      ...(queryShopsPermissions.data?.roles || []),
      ...(queryOrdersPermissions.data?.roles || []),
      ...(queryUserPermissions.data?.roles || []),
      ...(queryUserRoles.data?.roles || []),
    ],
    [
      queryCampaignsPermissions,
      queryLoyaltyPermissions,
      queryProductsPermissions,
      queryShopsPermissions,
      queryOrdersPermissions,
      queryUserPermissions,
      queryUserRoles,
    ]
  );

  const shops = useMemo(() => [...(queryUserPermissions.data?.shopExternalIds || [])], [queryUserPermissions]);

  return {
    isLoadingCampaigns: queryCampaignsPermissions.isLoading,
    isErrorCampaigns: queryCampaignsPermissions.isError,
    isLoadingLoyalty: queryLoyaltyPermissions.isLoading,
    isErrorLoyalty: queryLoyaltyPermissions.isError,
    isLoadingProducts: queryProductsPermissions.isLoading,
    isErrorProducts: queryProductsPermissions.isError,
    isLoadingShops: queryShopsPermissions.isLoading,
    isErrorShops: queryShopsPermissions.isError,
    isLoadingOrders: queryOrdersPermissions.isLoading,
    isErrorOrders: queryOrdersPermissions.isError,
    isErrorUsers: queryUserRoles.isError,
    isLoading:
      queryCampaignsPermissions.isLoading ||
      queryLoyaltyPermissions.isLoading ||
      queryProductsPermissions.isLoading ||
      queryShopsPermissions.isLoading ||
      queryOrdersPermissions.isLoading ||
      queryUserPermissions.isLoading ||
      queryUserRoles.isLoading,
    roles,
    shops,
  };
};
